import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import {
  Leaf,
  Shield,
  Users,
  CheckCircle,
  Star,
  ChevronRight,
  ChevronLeft,
  FileText,
  Heart,
  Award,
  AlertCircle
} from "lucide-react";

const STEPS = [
  { id: "intro", title: "Overview", icon: Star },
  { id: "doctrine", title: "Doctrinal", icon: Heart },
  { id: "ethics", title: "Ethics & Safety", icon: Shield },
  { id: "references", title: "Reference", icon: Users },
  { id: "outcomes", title: "Client Outcomes", icon: Award },
  { id: "review", title: "Review", icon: FileText }
];

const APOSTLES_CREED = `I believe in God, the Father almighty, creator of heaven and earth.

I believe in Jesus Christ, his only Son, our Lord, who was conceived by the Holy Spirit, born of the Virgin Mary, suffered under Pontius Pilate, was crucified, died, and was buried; he descended to the dead. On the third day he rose again; he ascended into heaven, he is seated at the right hand of the Father, and he will come to judge the living and the dead.

I believe in the Holy Spirit, the holy catholic Church, the communion of saints, the forgiveness of sins, the resurrection of the body, and the life everlasting.

Amen.`;

const PreferredCoachApplicationPage = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [existingApplication, setExistingApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const coachToken = localStorage.getItem("coach_token");
  
  const [formData, setFormData] = useState({
    // Doctrinal
    affirms_apostles_creed: false,
    doctrinal_statement: "",
    
    // Ethics & Safety
    ethics_commitment: "",
    boundary_approach: "",
    conflict_resolution: "",
    confidentiality_practices: "",
    
    // References
    references: [
      { name: "", relationship: "", email: "", phone: "" }
    ],
    
    // Outcomes
    typical_goals: "",
    success_stories: "",
    follow_up_approach: "",
    measurement_methods: ""
  });

  useEffect(() => {
    if (!coachToken) {
      navigate("/coach-login");
      return;
    }
    checkExistingApplication();
  }, [coachToken]);

  const checkExistingApplication = async () => {
    try {
      const response = await axios.get(`${API}/preferred-coach/application`, {
        headers: { Authorization: `Bearer ${coachToken}` }
      });
      if (response.data) {
        setExistingApplication(response.data);
      }
    } catch (error) {
      console.error("Error checking application:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updateReference = (index, field, value) => {
    setFormData(prev => {
      const refs = [...prev.references];
      refs[index] = { ...refs[index], [field]: value };
      return { ...prev, references: refs };
    });
  };

  const validateStep = () => {
    switch (currentStep) {
      case 1: // Doctrine
        if (!formData.affirms_apostles_creed) {
          toast.error("You must affirm the doctrinal statement to continue");
          return false;
        }
        return true;
      case 2: // Ethics
        if (!formData.ethics_commitment || !formData.boundary_approach || 
            !formData.conflict_resolution || !formData.confidentiality_practices) {
          toast.error("Please complete all ethics and safety questions");
          return false;
        }
        return true;
      case 3: // References
        {
          const ref = formData.references[0];
          if (!ref.name || !ref.relationship || !ref.email || !ref.phone) {
            toast.error("Please complete all fields for your professional reference");
            return false;
          }
        }
        return true;
      case 4: // Outcomes
        if (!formData.typical_goals || !formData.success_stories || 
            !formData.follow_up_approach || !formData.measurement_methods) {
          toast.error("Please complete all client outcomes questions");
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep()) {
      setCurrentStep(prev => Math.min(prev + 1, STEPS.length - 1));
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/preferred-coach/apply`, formData, {
        headers: { Authorization: `Bearer ${coachToken}` }
      });
      toast.success("Application submitted successfully!");
      navigate("/coach-dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit application");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
        <div className="animate-pulse text-[#7C9A92] text-xl">Loading...</div>
      </div>
    );
  }

  // Show status if already applied
  if (existingApplication) {
    return (
      <div className="min-h-screen bg-[#FAFAF9]">
        <header className="glass-header sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <Leaf className="w-8 h-8 text-[#7C9A92]" />
              <span className="font-serif text-2xl font-medium text-[#2C332B]">CoachBridge</span>
            </Link>
          </div>
        </header>
        
        <main className="max-w-2xl mx-auto px-6 py-12">
          <Card>
            <CardHeader className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#7C9A92]/10 flex items-center justify-center">
                <Star className="w-8 h-8 text-[#7C9A92]" />
              </div>
              <CardTitle className="font-serif text-2xl">Application Status</CardTitle>
              <CardDescription>Your Preferred Coach application</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="text-center">
                <Badge 
                  className={`text-sm px-4 py-2 ${
                    existingApplication.status === "approved" ? "bg-green-500" :
                    existingApplication.status === "rejected" ? "bg-red-500" :
                    existingApplication.status === "interview_requested" ? "bg-[#E09F7D]" :
                    "bg-[#7C9A92]"
                  }`}
                >
                  {existingApplication.status.replace(/_/g, " ").toUpperCase()}
                </Badge>
              </div>
              
              <div className="bg-stone-50 rounded-xl p-6 text-center">
                {existingApplication.status === "pending_review" && (
                  <p className="text-[#78716C]">
                    Your application is being reviewed by our team. We'll notify you via email once we've made a decision.
                  </p>
                )}
                {existingApplication.status === "interview_requested" && (
                  <p className="text-[#78716C]">
                    We'd like to schedule a video interview with you! Please check your email for instructions on how to schedule.
                  </p>
                )}
                {existingApplication.status === "interview_completed" && (
                  <p className="text-[#78716C]">
                    Thank you for completing the interview. Our team is making a final decision.
                  </p>
                )}
                {existingApplication.status === "approved" && (
                  <div className="space-y-4">
                    <p className="text-[#2C332B] font-medium">
                      Congratulations! Your application has been approved!
                    </p>
                    <p className="text-[#78716C]">
                      Complete your subscription to activate your preferred coach status.
                    </p>
                    <Button 
                      onClick={() => navigate("/coach-dashboard")}
                      className="rounded-full bg-[#7C9A92] hover:bg-[#6B8A82]"
                    >
                      Go to Dashboard
                    </Button>
                  </div>
                )}
                {existingApplication.status === "rejected" && (
                  <p className="text-[#78716C]">
                    {existingApplication.interview_notes || "We're unable to approve your application at this time. Please contact us if you have questions."}
                  </p>
                )}
              </div>
              
              <p className="text-xs text-[#78716C] text-center">
                Submitted on {new Date(existingApplication.created_at).toLocaleDateString()}
              </p>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Leaf className="w-8 h-8 text-[#7C9A92]" />
            <span className="font-serif text-2xl font-medium text-[#2C332B]">CoachBridge</span>
          </Link>
          <Button variant="ghost" onClick={() => navigate("/coach-dashboard")}>
            Back to Dashboard
          </Button>
        </div>
      </header>

      {/* Progress Steps */}
      <div className="bg-white border-b border-stone-100 py-4">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center justify-between">
            {STEPS.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <div className={`flex items-center gap-2 ${
                  index <= currentStep ? "text-[#7C9A92]" : "text-stone-300"
                }`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    index < currentStep ? "bg-[#7C9A92] text-white" :
                    index === currentStep ? "bg-[#7C9A92]/20 text-[#7C9A92]" :
                    "bg-stone-100 text-stone-400"
                  }`}>
                    {index < currentStep ? <CheckCircle className="w-5 h-5" /> : <step.icon className="w-4 h-4" />}
                  </div>
                  <span className="hidden md:block text-sm font-medium">{step.title}</span>
                </div>
                {index < STEPS.length - 1 && (
                  <div className={`w-8 md:w-16 h-0.5 mx-2 ${
                    index < currentStep ? "bg-[#7C9A92]" : "bg-stone-200"
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <main className="max-w-2xl mx-auto px-6 py-12">
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
        >
          {/* Step 0: Introduction */}
          {currentStep === 0 && (
            <Card>
              <CardHeader className="text-center">
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-[#7C9A92] to-[#E09F7D] flex items-center justify-center">
                  <Star className="w-10 h-10 text-white" />
                </div>
                <CardTitle className="font-serif text-3xl">Become a Preferred Coach</CardTitle>
                <CardDescription className="text-base">
                  Elevate your coaching practice with premium visibility
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid gap-4">
                  <div className="flex items-start gap-4 p-4 rounded-xl bg-[#7C9A92]/5">
                    <CheckCircle className="w-6 h-6 text-[#7C9A92] mt-0.5" />
                    <div>
                      <h4 className="font-medium text-[#2C332B]">Priority Placement</h4>
                      <p className="text-sm text-[#78716C]">Appear first in search results and recommended coaches lists</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 p-4 rounded-xl bg-[#7C9A92]/5">
                    <CheckCircle className="w-6 h-6 text-[#7C9A92] mt-0.5" />
                    <div>
                      <h4 className="font-medium text-[#2C332B]">Preferred Badge</h4>
                      <p className="text-sm text-[#78716C]">Stand out with a verified preferred coach badge on your profile</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 p-4 rounded-xl bg-[#7C9A92]/5">
                    <CheckCircle className="w-6 h-6 text-[#7C9A92] mt-0.5" />
                    <div>
                      <h4 className="font-medium text-[#2C332B]">Enhanced Trust</h4>
                      <p className="text-sm text-[#78716C]">Additional vetting gives clients confidence in your expertise</p>
                    </div>
                  </div>
                </div>

                <div className="bg-[#F5F1E8] rounded-xl p-6">
                  <h4 className="font-medium text-[#2C332B] mb-2">Pricing</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-white rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-[#2C332B]">$49</p>
                      <p className="text-sm text-[#78716C]">per month</p>
                    </div>
                    <div className="bg-white rounded-lg p-4 text-center border-2 border-[#7C9A92]">
                      <p className="text-2xl font-bold text-[#2C332B]">$500</p>
                      <p className="text-sm text-[#78716C]">per year (save $88)</p>
                    </div>
                  </div>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <div className="flex gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                    <div className="text-sm text-amber-800">
                      <p className="font-medium">Requirements</p>
                      <ul className="mt-1 space-y-1 text-amber-700">
                        <li>• Affirm doctrinal statement</li>
                        <li>• Complete ethics & safety questionnaire</li>
                        <li>• Provide 1 professional reference</li>
                        <li>• Pass a video interview</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 1: Doctrinal */}
          {currentStep === 1 && (
            <Card>
              <CardHeader>
                <CardTitle className="font-serif text-2xl flex items-center gap-2">
                  <Heart className="w-6 h-6 text-[#E09F7D]" />
                  Doctrinal Conviction
                </CardTitle>
                <CardDescription>
                  As a ministry-focused platform, we ask coaches to affirm foundational Christian beliefs
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="bg-stone-50 rounded-xl p-6">
                  <h4 className="font-medium text-[#2C332B] mb-3">Doctrinal Statement</h4>
                  <p className="text-sm text-[#78716C] whitespace-pre-line leading-relaxed">
                    {APOSTLES_CREED}
                  </p>
                </div>

                <div className="flex items-start gap-3 p-4 rounded-xl border border-stone-200">
                  <Checkbox
                    id="affirm-creed"
                    checked={formData.affirms_apostles_creed}
                    onCheckedChange={(checked) => updateFormData("affirms_apostles_creed", checked)}
                    data-testid="affirm-creed-checkbox"
                  />
                  <label htmlFor="affirm-creed" className="text-sm text-[#2C332B] leading-relaxed cursor-pointer">
                    I affirm the above as an accurate summary of essential Christian doctrine and commit to upholding these beliefs in my coaching practice.
                  </label>
                </div>

                <div className="space-y-2">
                  <Label className="text-[#2C332B]">
                    Additional Doctrinal Statement (Optional)
                  </Label>
                  <Textarea
                    placeholder="Share any additional details about your theological background or denominational affiliation..."
                    value={formData.doctrinal_statement}
                    onChange={(e) => updateFormData("doctrinal_statement", e.target.value)}
                    className="min-h-[100px] rounded-xl"
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 2: Ethics & Safety */}
          {currentStep === 2 && (
            <Card>
              <CardHeader>
                <CardTitle className="font-serif text-2xl flex items-center gap-2">
                  <Shield className="w-6 h-6 text-[#7C9A92]" />
                  Ethics & Client Safety
                </CardTitle>
                <CardDescription>
                  The safety and honor of clients is of utmost importance
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label className="text-[#2C332B] font-medium">
                    How do you ensure the safety and well-being of your clients? *
                  </Label>
                  <Textarea
                    placeholder="Describe your approach to creating a safe coaching environment..."
                    value={formData.ethics_commitment}
                    onChange={(e) => updateFormData("ethics_commitment", e.target.value)}
                    className="min-h-[100px] rounded-xl"
                    data-testid="ethics-commitment"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-[#2C332B] font-medium">
                    How do you maintain appropriate professional boundaries? *
                  </Label>
                  <Textarea
                    placeholder="Describe how you establish and maintain boundaries in coaching relationships..."
                    value={formData.boundary_approach}
                    onChange={(e) => updateFormData("boundary_approach", e.target.value)}
                    className="min-h-[100px] rounded-xl"
                    data-testid="boundary-approach"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-[#2C332B] font-medium">
                    How do you handle conflicts or difficult situations with clients? *
                  </Label>
                  <Textarea
                    placeholder="Describe your approach to conflict resolution and challenging coaching moments..."
                    value={formData.conflict_resolution}
                    onChange={(e) => updateFormData("conflict_resolution", e.target.value)}
                    className="min-h-[100px] rounded-xl"
                    data-testid="conflict-resolution"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-[#2C332B] font-medium">
                    What are your confidentiality practices? *
                  </Label>
                  <Textarea
                    placeholder="Describe how you protect client privacy and handle sensitive information..."
                    value={formData.confidentiality_practices}
                    onChange={(e) => updateFormData("confidentiality_practices", e.target.value)}
                    className="min-h-[100px] rounded-xl"
                    data-testid="confidentiality-practices"
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 3: References */}
          {currentStep === 3 && (
            <Card>
              <CardHeader>
                <CardTitle className="font-serif text-2xl flex items-center gap-2">
                  <Users className="w-6 h-6 text-[#7C9A92]" />
                  Professional Reference
                </CardTitle>
                <CardDescription>
                  Please provide 1 professional reference who can speak to your coaching abilities
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-8">
                {[0].map((index) => (
                  <div key={index} className="space-y-4 p-4 rounded-xl bg-stone-50">
                    <h4 className="font-medium text-[#2C332B]">Reference</h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Full Name *</Label>
                        <Input
                          placeholder="John Smith"
                          value={formData.references[index].name}
                          onChange={(e) => updateReference(index, "name", e.target.value)}
                          className="rounded-xl"
                          data-testid={`ref-${index}-name`}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Relationship *</Label>
                        <Input
                          placeholder="Former client, Colleague, Supervisor..."
                          value={formData.references[index].relationship}
                          onChange={(e) => updateReference(index, "relationship", e.target.value)}
                          className="rounded-xl"
                          data-testid={`ref-${index}-relationship`}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Email *</Label>
                        <Input
                          type="email"
                          placeholder="john@example.com"
                          value={formData.references[index].email}
                          onChange={(e) => updateReference(index, "email", e.target.value)}
                          className="rounded-xl"
                          data-testid={`ref-${index}-email`}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Phone *</Label>
                        <Input
                          placeholder="(555) 123-4567"
                          value={formData.references[index].phone}
                          onChange={(e) => updateReference(index, "phone", e.target.value)}
                          className="rounded-xl"
                          data-testid={`ref-${index}-phone`}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Step 4: Client Outcomes */}
          {currentStep === 4 && (
            <Card>
              <CardHeader>
                <CardTitle className="font-serif text-2xl flex items-center gap-2">
                  <Award className="w-6 h-6 text-[#E09F7D]" />
                  Client Outcomes
                </CardTitle>
                <CardDescription>
                  Tell us about the transformation you help clients achieve
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label className="text-[#2C332B] font-medium">
                    What typical goals do your clients achieve? *
                  </Label>
                  <Textarea
                    placeholder="Describe the common goals and outcomes you help clients reach..."
                    value={formData.typical_goals}
                    onChange={(e) => updateFormData("typical_goals", e.target.value)}
                    className="min-h-[100px] rounded-xl"
                    data-testid="typical-goals"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-[#2C332B] font-medium">
                    Share 1-2 success stories (anonymized) *
                  </Label>
                  <Textarea
                    placeholder="Describe transformations you've helped clients experience (without identifying details)..."
                    value={formData.success_stories}
                    onChange={(e) => updateFormData("success_stories", e.target.value)}
                    className="min-h-[120px] rounded-xl"
                    data-testid="success-stories"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-[#2C332B] font-medium">
                    How do you support ongoing growth after coaching ends? *
                  </Label>
                  <Textarea
                    placeholder="Describe your approach to follow-up and continuing client development..."
                    value={formData.follow_up_approach}
                    onChange={(e) => updateFormData("follow_up_approach", e.target.value)}
                    className="min-h-[100px] rounded-xl"
                    data-testid="follow-up-approach"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-[#2C332B] font-medium">
                    How do you measure progress with clients? *
                  </Label>
                  <Textarea
                    placeholder="Describe the methods you use to track and measure client progress..."
                    value={formData.measurement_methods}
                    onChange={(e) => updateFormData("measurement_methods", e.target.value)}
                    className="min-h-[100px] rounded-xl"
                    data-testid="measurement-methods"
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 5: Review */}
          {currentStep === 5 && (
            <Card>
              <CardHeader>
                <CardTitle className="font-serif text-2xl flex items-center gap-2">
                  <FileText className="w-6 h-6 text-[#7C9A92]" />
                  Review & Submit
                </CardTitle>
                <CardDescription>
                  Please review your application before submitting
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div className="p-4 rounded-xl bg-stone-50">
                    <h4 className="font-medium text-[#2C332B] mb-2">Doctrinal Affirmation</h4>
                    <p className="text-sm text-[#78716C]">
                      {formData.affirms_apostles_creed ? "✓ Doctrinal statement affirmed" : "✗ Not affirmed"}
                    </p>
                  </div>

                  <div className="p-4 rounded-xl bg-stone-50">
                    <h4 className="font-medium text-[#2C332B] mb-2">Ethics & Safety</h4>
                    <p className="text-sm text-[#78716C]">4 responses provided</p>
                  </div>

                  <div className="p-4 rounded-xl bg-stone-50">
                    <h4 className="font-medium text-[#2C332B] mb-2">Reference</h4>
                    {formData.references.map((ref, i) => (
                      <p key={i} className="text-sm text-[#78716C]">
                        {ref.name || "Not provided"} ({ref.relationship || "No relationship"})
                      </p>
                    ))}
                  </div>

                  <div className="p-4 rounded-xl bg-stone-50">
                    <h4 className="font-medium text-[#2C332B] mb-2">Client Outcomes</h4>
                    <p className="text-sm text-[#78716C]">4 responses provided</p>
                  </div>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <div className="flex gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600 shrink-0" />
                    <p className="text-sm text-amber-800">
                      After submission, our team will review your application. If approved for the next stage, you'll receive an email to schedule a video interview.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </motion.div>

        {/* Navigation */}
        <div className="flex justify-between mt-8">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={currentStep === 0}
            className="rounded-full"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back
          </Button>

          {currentStep < STEPS.length - 1 ? (
            <Button
              onClick={handleNext}
              className="rounded-full bg-[#7C9A92] hover:bg-[#6B8A82]"
              data-testid="next-step-btn"
            >
              Continue
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="rounded-full bg-[#7C9A92] hover:bg-[#6B8A82]"
              data-testid="submit-application-btn"
            >
              {submitting ? "Submitting..." : "Submit Application"}
            </Button>
          )}
        </div>
      </main>
    </div>
  );
};

export default PreferredCoachApplicationPage;
